/**
 * X-Ladder-Context header builder (Mike's spec, see LadderDev/ladder-web#2214).
 *
 * Identifies the source of Rocky API traffic. Format is logfmt `key=value`
 * pairs, space-separated. Standard keys: product, productVer, component,
 * componentVer, subcomponent, subcomponentVer — all optional. Keys are
 * alphanumeric camelCase; values are alphanumeric plus dashes, underscores
 * and periods. Case sensitive.
 *
 * The header is purely additive — nothing in here may ever throw, and a
 * request must succeed whether or not the header (or any individual key)
 * is present.
 *
 * This module is intentionally dependency-free so it can run in Node,
 * Edge middleware and the browser, and so it can move to the unified
 * Rocky server client planned in packages/server-utils (#2202) without
 * dragging anything along.
 */

export const X_LADDER_CONTEXT_HEADER = "X-Ladder-Context";

export type LadderContextFields = Record<string, string | undefined> & {
  product?: string;
  productVer?: string;
  component?: string;
  componentVer?: string;
  subcomponent?: string;
  subcomponentVer?: string;
};

// Keys: alphanumeric camelCase (must start with a lowercase letter).
const KEY_RE = /^[a-z][a-zA-Z0-9]*$/;
// Values: strip anything that isn't alphanumeric, dash, underscore or period.
const VALUE_ILLEGAL_RE = /[^a-zA-Z0-9._-]/g;

/**
 * Build the logfmt header value from a set of fields.
 *
 * Keys that don't match the charset are dropped; values have illegal
 * characters stripped and are dropped if nothing remains. Returns "" when
 * no fields survive — callers should omit the header entirely in that case
 * (or use ladderContextHeaders, which does it for them).
 */
export function buildLadderContext(fields: LadderContextFields): string {
  try {
    const parts: string[] = [];
    for (const [key, raw] of Object.entries(fields)) {
      if (raw === undefined || raw === null) continue;
      if (!KEY_RE.test(key)) continue;
      const value = String(raw).replace(VALUE_ILLEGAL_RE, "");
      if (!value) continue;
      parts.push(`${key}=${value}`);
    }
    return parts.join(" ");
  } catch {
    // Never let context-building break a request.
    return "";
  }
}

/**
 * Short git SHA for productVer, from Vercel's system env vars.
 * VERCEL_GIT_COMMIT_SHA on the server; the NEXT_PUBLIC_ variant is what
 * gets inlined into client bundles. Undefined locally — the key is simply
 * omitted from the header.
 */
export function ladderContextProductVer(): string | undefined {
  const sha =
    process.env.VERCEL_GIT_COMMIT_SHA ||
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA;
  return sha ? sha.slice(0, 8) : undefined;
}

/**
 * The consuming app's product identifier, for call sites inside shared
 * packages (queries is imported by all four apps). Each app hardcodes
 * NEXT_PUBLIC_LADDER_PRODUCT in its next.config env block, which Next.js
 * inlines per-app at build time; NEXT_PUBLIC_APP_NAME (lowercased) is the
 * fallback signal. App-local call sites should pass their product
 * explicitly instead.
 */
export function ladderContextProduct(): string | undefined {
  const product =
    process.env.NEXT_PUBLIC_LADDER_PRODUCT ||
    process.env.NEXT_PUBLIC_APP_NAME?.toLowerCase();
  return product || undefined;
}

/**
 * Convenience wrapper: returns `{ "X-Ladder-Context": value }` ready to
 * spread into a headers object, or `{}` when there's nothing to send.
 * Fills in productVer from the Vercel git SHA unless the caller provided
 * the key themselves.
 */
export function ladderContextHeaders(
  fields: LadderContextFields
): Record<string, string> {
  try {
    const withVer: LadderContextFields =
      "productVer" in fields
        ? fields
        : { ...fields, productVer: ladderContextProductVer() };
    const value = buildLadderContext(withVer);
    return value ? { [X_LADDER_CONTEXT_HEADER]: value } : {};
  } catch {
    return {};
  }
}
